// Stats Table (?live=1; Ben, 2026-11-02: Catalyst's Leagues → Players table, "Let's just build
// it"; docs/modules/stats-table.md). Many stats side by side for every player in the group, one row
// each, sorted by any heading. The group is the Ranking's (the League chip's league files, the same
// chips: rules.md Row 2); the columns are a Radar template's spokes or the user's own list (the
// Radar's spoke editor). Rows drag out as players; a stat heading's table icon drags out a Ranking
// of that stat in the same leagues and season.
//
// needsPlayer:false, as the Ranking: it opens on the canvas player's league + season
// (`_statsTableSourcePlayer`, from the sticky pick), that player's row highlighted. Instance state
// (mod._statsTable*) is captured by saveState / Undo / presets.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { ModuleExplainer, shortNameOrPlayer } from "./ModuleExplainer.js";
import { explainStatsTable } from "./explain.js";
import { AxisEditor, RadarEditAxesButton, type AxisEditorGroup } from "./RadarAxisEditor.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { humanizePositionTemplate } from "./radar-view.js";
import { isLeagueCleared, useLeagueChip } from "../hooks/useLeagueChip.js";
import { useGenderSwitch } from "../hooks/useGenderSwitch.js";
import { womensOf } from "../live/gender-switch.js";
import { GenderSwitch } from "./GenderSwitch.js";
import { statsTableCatalog, statsTableCategories, useStatsTableBoard } from "../hooks/useStatsTable.js";
import { rankingCategoryOf } from "../hooks/useRankings.js";
import {
  CUSTOM_TEMPLATE,
  DEFAULT_SORT,
  STATS_TABLE_TEMPLATES,
  defaultTemplateFor,
  nextSort,
  sortRows,
  tableColumns,
  type SortColumn,
  type StatsTableRow,
  type TableSort,
} from "../live/stats-table.js";
import { formatValueForMode, hasTotalMode, type ActiveFilters } from "../engines/stats-filter-kit.js";
import { LIVE } from "../live/flag.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { startMenuDrag, startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { undoApi } from "../canvas/undo.js";
import { noteRowPlayer } from "../live/player-gender.js";
import type { PlayerId } from "../data/players.js";

/** The table icon a stat heading drags out by (the Stats tiles' and Radar's rank icon). */
function RankIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="1.5" y="2.5" width="13" height="11" rx="1.2" />
      <line x1="1.5" y1="6.17" x2="14.5" y2="6.17" />
      <line x1="1.5" y1="9.83" x2="14.5" y2="9.83" />
      <line x1="6.5" y1="2.5" x2="6.5" y2="13.5" />
    </svg>
  );
}

const FIXED: readonly { column: SortColumn; label: string; numeric: boolean }[] = [
  { column: "name", label: "Player", numeric: false },
  { column: "team", label: "Club", numeric: false },
  { column: "position", label: "Position", numeric: false },
  { column: "age", label: "Age", numeric: true },
  { column: "hops", label: "HOPS", numeric: true },
  { column: "minutes", label: "Minutes", numeric: true },
];

/** The HOPS cell: its percentile among the player's gender (as Catalyst's "HOPS Percentile"
 *  column), else the rating itself (×100, as the Bio), else "—". */
function hopsText(r: StatsTableRow): string {
  if (r.hopsPercentile !== null) return String(r.hopsPercentile);
  return r.hops === null ? "—" : (r.hops * 100).toFixed(1);
}

export function StatsTableModule(props: ProtoModuleProps) {
  // Real data only: its group is the nightly Stats files (the snapshot site hides its tile).
  if (!LIVE) return <div className="stats-empty">The Stats Table works on real data only.</div>;
  return <LiveStatsTable {...props} />;
}

function LiveStatsTable({ mod, body }: ProtoModuleProps) {
  const categories = useMemo(() => statsTableCategories(), []);
  const catalog = useMemo(() => statsTableCatalog(categories), [categories]);

  // The player it opened for: their league + season are the default group (as a Ranking's).
  const [sourcePlayer] = useState<PlayerId>(() => {
    const existing = getModState<PlayerId>(mod, "_statsTableSourcePlayer");
    if (existing) return existing;
    const sticky = getStickyPlayer();
    if (sticky === null) throw new Error("A Stats Table needs a player to take its league from");
    setModState(mod, "_statsTableSourcePlayer", sticky);
    return sticky;
  });
  const [seasonPick, setSeasonPick] = useState<string | null>(() => getModState<string>(mod, "_statsTableSeason") ?? null);
  const [mode, setMode] = useState<string>(() => (getModState<string>(mod, "_statsTableValueMode") === "total" ? "total" : "per90"));
  const [, setVersion] = useState(0);
  const rerender = useCallback(() => setVersion((v) => v + 1), []);

  // The chips' state, edited in place by the chip bar (as the Ranking's `_rankingFilters`).
  const [activeFilters] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_statsTableFilters");
    if (existing) return existing;
    const fresh: ActiveFilters = {};
    setModState(mod, "_statsTableFilters", fresh);
    return fresh;
  });
  const [filterVersion, setFilterVersion] = useState(0);
  const activeSnapshot = useMemo<ActiveFilters>(() => ({ ...activeFilters }), [activeFilters, filterVersion]);
  const bumpFilters = useCallback(() => setFilterVersion((v) => v + 1), []);

  // The columns: the template's spokes, or the user's own list once edited ("Custom"). A new table
  // takes the canvas player's main position as its template (set below, once the group is read).
  const custom = getModState<string[]>(mod, "_statsTableColumns");
  const template = getModState<string>(mod, "_statsTableTemplate") ?? (custom?.length ? CUSTOM_TEMPLATE : "midfielder");
  const columns = tableColumns(template, template === CUSTOM_TEMPLATE ? custom : null, catalog);

  // The Men's / Women's switch: a flip lands on the other gender's top league, its latest season.
  const resetSeason = useCallback(() => {
    setSeasonPick(null);
    setModState(mod, "_statsTableSeason", undefined);
  }, [mod]);
  const gender = useGenderSwitch(mod, "_statsTableGender", "_statsTableLeagueQuiet", activeFilters, bumpFilters, resetSeason);

  const leagueCleared = isLeagueCleared(mod, activeFilters, "_statsTableLeagueQuiet");
  const board = useStatsTableBoard({
    sourcePlayer,
    leagueCleared,
    season: seasonPick,
    columns,
    useTotal: mode === "total",
    active: activeSnapshot,
    womensPick: gender.pick ? womensOf(gender.pick) : null,
  });
  useEffect(() => {
    if (getModState<string>(mod, "_statsTableTemplate") !== undefined || board.count === null) return;
    setModState(mod, "_statsTableTemplate", defaultTemplateFor(board.refPosition));
    rerender();
  }, [mod, board.count, board.refPosition, rerender]);

  const { onChipsChange, quietKeys } = useLeagueChip(mod, activeFilters, board.defaultLeague, filterVersion, bumpFilters, "_statsTableLeagueQuiet");

  const savedSort = getModState<TableSort>(mod, "_statsTableSort");
  const sort = savedSort && (FIXED.some((f) => f.column === savedSort.column) || columns.includes(savedSort.column)) ? savedSort : DEFAULT_SORT;
  const rows = useMemo(() => sortRows(board.rows, sort), [board.rows, sort.column, sort.dir]);

  useEffect(() => {
    setModState(mod, "_statsTableSeason", board.season);
  }, [mod, board.season]);
  useEffect(() => {
    setModState(mod, "_statsTableValueMode", mode);
  }, [mod, mode]);

  // The table scrolls inside the module (both ways), its header kept in view.
  useEffect(() => {
    const prev = { justify: body.style.justifyContent, overflow: body.style.overflowY };
    body.style.justifyContent = "flex-start";
    body.style.overflowY = "hidden";
    return () => {
      body.style.justifyContent = prev.justify;
      body.style.overflowY = prev.overflow;
    };
  }, [body]);

  // The canvas player's row: highlighted, and scrolled to once it's there.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledTo = useRef(false);
  useLayoutEffect(() => {
    if (scrolledTo.current) return;
    const row = scrollRef.current?.querySelector<HTMLElement>(".stats-table-row-own");
    if (!row) return;
    scrolledTo.current = true;
    row.scrollIntoView({ block: "center" });
  }, [rows]);

  const editBtnRef = useRef<HTMLButtonElement>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const editorGroups = useMemo<AxisEditorGroup[]>(
    () => categories.map((c) => ({ id: c.id, name: c.name, metrics: c.subcategories.flatMap((s) => s.playerMetrics) })),
    [categories],
  );
  const labelOf = (key: string) => {
    const m = catalog[key];
    return m ? m.shortName || m.name : key;
  };

  const onTemplateChange = (next: string) => {
    if (next === template) return;
    undoApi().saveState();
    setModState(mod, "_statsTableTemplate", next);
    // A template replaces the user's own list ("Custom" keeps it).
    if (next !== CUSTOM_TEMPLATE) setModState(mod, "_statsTableColumns", undefined);
    setModState(mod, "_statsTableSort", undefined);
    setEditorOpen(false);
    rerender();
  };
  const onColumnsEdit = (next: string[]) => {
    undoApi().saveState();
    setModState(mod, "_statsTableColumns", next);
    setModState(mod, "_statsTableTemplate", CUSTOM_TEMPLATE);
    rerender();
  };
  const onSort = (column: SortColumn) => {
    undoApi().saveState();
    setModState(mod, "_statsTableSort", nextSort(sort, column));
    rerender();
  };

  // A row drags out as the player (→ a Bio on empty canvas, or onto a module), as a Ranking's.
  const onRowPointerDown = (e: ReactPointerEvent<HTMLTableRowElement>, row: StatsTableRow) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    noteRowPlayer({ id: row.playerId, name: row.name, team: row.team }, board.womens);
    setStickyPlayer(row.playerId);
    startPlayerTokenDrag(e.nativeEvent);
  };
  // A stat heading's icon drags out a Ranking of it: the same leagues and season, this table's
  // player found in it (as a Stats tile's stat does, #352).
  const onRankDrag = (e: ReactPointerEvent<HTMLElement>, key: string) => {
    const categoryId = rankingCategoryOf(key);
    if (categoryId === null || window.innerWidth <= 768 || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    startMenuDrag(e.nativeEvent, "Ranking", 3, 5, {
      _rankingCategoryId: categoryId,
      _rankingMetricKey: key,
      _rankingSeason: board.season,
      _rankingSourcePlayer: sourcePlayer,
      _rankingJumpedPlayer: sourcePlayer,
      ...(activeFilters.league !== undefined ? { _rankingFilters: { league: structuredClone(activeFilters.league) } } : {}),
    });
  };

  const useTotal = mode === "total";
  const seasonLabel = humanizeSeasonCode(board.season);
  const seasonItems: StyledSelectItem[] = board.seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) }));
  const templateItems: StyledSelectItem[] = [
    ...STATS_TABLE_TEMPLATES.map((t) => ({ value: t, label: humanizePositionTemplate(t) })),
    ...(template === CUSTOM_TEMPLATE ? [{ value: CUSTOM_TEMPLATE, label: "Custom" }] : []),
  ];
  const anyTotal = columns.some((k) => hasTotalMode({ key: k }));
  // The readout names what's shown: "312 players · Premier League 26/27".
  const where = board.leagues.length === 1 ? `${board.leagues[0]} ${seasonLabel}` : board.leagues.length > 1 ? `${board.leagues.length} leagues, ${seasonLabel}` : "";
  const label = board.count === null ? board.label : where ? `${board.label} · ${where}` : board.label;
  const sortName = FIXED.find((f) => f.column === sort.column)?.label ?? catalog[sort.column]?.name ?? sort.column;
  const filteredOut = board.count === 0;
  // The player's row is marked only on their own gender's side of the switch.
  const onPlayersSide = board.gender.shown === board.gender.player;

  return (
    <>
      <ModuleExplainer
        mod={mod}
        paragraphs={explainStatsTable({
          season: seasonLabel,
          mode: useTotal ? "total" : "per90",
          filters: activeSnapshot,
          refName: shortNameOrPlayer(sourcePlayer),
          count: board.count,
          label: board.count === null ? board.label : null,
          template: template === CUSTOM_TEMPLATE ? null : humanizePositionTemplate(template),
          columns: columns.length,
          sortedBy: sortName,
          ascending: sort.dir === "asc",
          switchedTo: onPlayersSide ? null : board.gender.shown,
        })}
      />
      <SubControls mod={mod} body={body}>
        <StyledSelect
          items={seasonItems}
          value={board.season}
          onChange={(next) => {
            setSeasonPick(next);
            setModState(mod, "_statsTableSeason", next);
          }}
          wrapClass="season-select-wrap"
          extraClass="season-select"
        />
        <StyledSelect items={templateItems} value={template} onChange={onTemplateChange} wrapClass="template-select-wrap" extraClass="template-select" />
        <GenderSwitch womens={board.gender.shown} onFlip={() => gender.flip(board.gender.shown, board.gender.player)} />
        <RadarEditAxesButton open={editorOpen} btnRef={editBtnRef} onToggle={() => setEditorOpen((o) => !o)} />
      </SubControls>
      {editorOpen && editBtnRef.current && (
        <AxisEditor
          anchor={editBtnRef.current}
          initialKeys={columns}
          groups={editorGroups}
          labelOf={labelOf}
          onPersist={onColumnsEdit}
          onClose={() => setEditorOpen(false)}
          minKeys={1}
          heading="In this table"
        />
      )}
      <PopulationReadout
        mod={mod}
        body={body}
        count={board.count}
        showToggle={anyTotal}
        mode={mode}
        onToggle={() => setMode((m) => (m === "total" ? "per90" : "total"))}
        label={label}
      />
      <div className="stats-table-wrap">
        <div className="filter-bar">
          <FilterChipBar
            active={activeFilters}
            refPlayer={board.chips.refPlayer}
            options={board.chips.options}
            rangeFilters={board.chips.rangeFilters}
            onChange={onChipsChange}
            addMenuKeys={board.chips.addMenuKeys}
            newChipValue={board.chips.newChipValue}
            quietKeys={quietKeys}
            optionInfo={board.chips.optionInfo}
          />
        </div>
        {board.count === null ? null : filteredOut ? (
          <div className="stats-empty">No players in {where || seasonLabel} match these chips. Remove one to widen the table.</div>
        ) : (
          <div className="stats-table-scroll" ref={scrollRef}>
            <table className="stats-table">
              <thead>
                <tr>
                  {FIXED.map((f) => (
                    <th
                      key={f.column}
                      className={`stats-table-th${f.numeric ? " num" : ""}${sort.column === f.column ? " sorted" : ""}`}
                      onClick={() => onSort(f.column)}
                    >
                      {f.label}
                      {sort.column === f.column && <span className="stats-table-arrow">{sort.dir === "asc" ? "▲" : "▼"}</span>}
                    </th>
                  ))}
                  {columns.map((k) => (
                    <th key={k} className={`stats-table-th num${sort.column === k ? " sorted" : ""}`} onClick={() => onSort(k)}>
                      <span className="stats-table-th-inner">
                        <span className="pbar-rank-icon stats-table-rank" onPointerDown={(e) => onRankDrag(e, k)} onClick={(e) => e.stopPropagation()}>
                          <RankIcon />
                        </span>
                        {labelOf(k)}
                        {sort.column === k && <span className="stats-table-arrow">{sort.dir === "asc" ? "▲" : "▼"}</span>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.key}
                    className={`stats-table-row${onPlayersSide && r.playerId === sourcePlayer ? " stats-table-row-own" : ""}`}
                    onPointerDown={(e) => onRowPointerDown(e, r)}
                  >
                    <td className="stats-table-name">{r.shortName}</td>
                    <td className="stats-table-text">{r.team ?? "—"}</td>
                    <td className="stats-table-text">{r.position}</td>
                    <td className="num">{r.age ?? "—"}</td>
                    <td className="num">{hopsText(r)}</td>
                    <td className="num">{r.minutes === null ? "—" : Math.round(r.minutes).toLocaleString()}</td>
                    {columns.map((k) => (
                      <td key={k} className="num">
                        {r.values[k] === null ? "—" : formatValueForMode(r.values[k], catalog[k] ?? { key: k }, useTotal ? "total" : "per90")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
